import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import { HDate } from '@hebcal/core';
import HeaderComp from '../Header/Header';
import utils from '../../Functions/utils';
import { getFormattedDate } from '../../Functions/getFormattedDate';

import 'react-calendar/dist/Calendar.css';
import './profile.css';
import EditProfile from '../EditProfile/EditProfile';
import ReportToRabbiComp from '../reportToRabbi/reportToRabbi';
import FavoriteTehilimComp from '../myFavoriteTehilim/myFavoriteTehilim';



const convertDayOfToDatePrefix = (dayOf) => {
  if (!/^\d{8}$/.test(dayOf)) return null; 

  const day = dayOf.slice(0, 2);
  const month = dayOf.slice(2, 4);
  const year = dayOf.slice(4);

  // day ו-month כבר אמורים להיות עם מובילי אפסים כי הם 2 תווים
  return `${year}-${month}-${day}`;  // למשל: "2025-07-03"
};


  // const groupByDate = (chapters) => {
  //   const grouped = {};
  //   chapters.forEach(item => {
  //     const date = convertDayOfToDatePrefix(item.dayOf);
  //     if (!date) return;
  //     if (!grouped[date]) grouped[date] = [];
  //     grouped[date].push(item.chapterId);
  //   });
  //   return grouped;
  // };

function ProfileComp() {
  const navigate = useNavigate();

  // ---------- State ----------
  const [userData, setUserData] = useState({});
  const [chabadBooks, setChabadBooks] = useState({});
  const [userCompletedData, setUserCompletedData] = useState([]);
  const [completedDays, setCompletedDays] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    calendar: false,
    profile: false,
    report: false,
    myFavoriteTehilim: false
  });

  const grouped = useMemo(() => {
    const result = {};
    // console.log(userCompletedData)
    userCompletedData.forEach(item => {
      const dateStr = new Date(convertDayOfToDatePrefix(item.dayOf)).toISOString().slice(0, 10);
      if (!result[dateStr]) {
        result[dateStr] = [];
      }
      result[dateStr].push(item);
    });
    return result;
  }, [userCompletedData]);


  const [openNodes, setOpenNodes] = useState({});
  const [menuHeaderOpen, setMenuHeaderOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());


  useEffect(() => {
    const fetchData = async () => {
      await fetchCompletedChapters();
    };
    fetchData();
  }, [selectedDate]);

  // ---------- useEffect - טענת הערות ----------
  useEffect(() => {
    const savedNotes = localStorage.getItem('userNotes') || '';
    setNotes(savedNotes);
  }, []);

  useEffect(() => {
    localStorage.setItem('userNotes', notes);
  }, [notes]);

  // ---------- useEffect - טענת מידע מהשרת ----------
  useEffect(() => {
    const fetchData = async () => {
      await fetchUserData();
      await fetchCompletedChapters();
      await fetchBooksData();
    };
    fetchData();
  }, []);

  const fetchUserData = async () => {
    const localKey = 'userData';
    try {
      const cached = localStorage.getItem(localKey);
      if (cached) {
        setUserData(JSON.parse(cached));
      } else {
        const resp = await utils.requestData('GetUserData', {});
        // if (resp[0].code !== 90) throw new Error(resp[0].msg);
        setUserData(resp[0]);
        localStorage.setItem(localKey, JSON.stringify(resp[0]));
      }
    } catch (err) {
      console.error('Error fetching userData:', err);
      setError(err.message);
    }
  };

  const fetchCompletedChapters = async () => {
    try {
      const resp = await utils.requestData('GetUserCompletedChapters', {
        dayOf: getFormattedDate(selectedDate, false)
      });

      const rawJson = resp[0]?.chapterListJson;
      if (!rawJson) {
        // console.warn("chapterListJson is missing or null");
        setUserCompletedData([]);
        setCompletedDays([]);
        return;
      }

      const parsed = JSON.parse(rawJson);
      if (!Array.isArray(parsed)) {
        console.warn("Parsed data is not an array:", parsed);
        setUserCompletedData([]);
        setCompletedDays([]);
        return;
      }

      // console.warn(parsed);
      setUserCompletedData(parsed);

      const uniqueChapters = [...new Set(parsed.map(c => c.chapterId))];
      // console.warn(uniqueChapters);
      setCompletedDays(uniqueChapters);
    } catch (err) {
      console.error('Error fetching completed chapters:', err);
      setError(err.message);
    }
  };

  const fetchBooksData = async () => {
    try {
      const resp = await utils.requestData('GetBooksData', {});
      // if (resp[0].code !== 90) throw new Error(resp[0].msg);
      const cleanJson = resp[0].jsonValue.replace(/,\s*([}\]])/g, '$1');
      setChabadBooks(JSON.parse(cleanJson));
    } catch (err) {
      console.error('Error fetching books data:', err);
      setError(err.message);
    }
  };

  // ---------- Helper Functions ----------
  const toggleNode = (path) => setOpenNodes(prev => ({ ...prev, [path]: !prev[path] }));

  const toggleSection = (name) => setSectionsOpen(prev => ({ ...prev, [name]: !prev[name] }));

  

  const getHebrewDateString = (date, grouped = {}) => {
  try {
    const hdate = new HDate(date);
    const full = hdate.renderGematriya(); 
    const withoutYear = full.replace(/\s+תש.+"?$/, '');
    const withoutNikud = withoutYear.normalize('NFD').replace(/[\u0591-\u05BD\u05BF-\u05C7]/g, '');

    const dateStr = date.toISOString().slice(0, 10);
    const chapterCount = grouped[dateStr]?.length || 0;

    return chapterCount > 0
      ? `${withoutNikud}`
      : withoutNikud;
  } catch (err) {
    return '';
  }
};


  // ---------- Render Tree View ----------
  const renderBooks = (data, path = '', depth = 0) => {
    const order = data.__order || Object.keys(data);
    return (
      <ul className="book-list" style={{ paddingRight: depth * 12 }}>
        {order
          .filter(key => key !== '__order')
          .map((key) => {
            const value = data[key];
            const currentPath = path ? `${path}/${key}` : key;
            const isOpen = openNodes[currentPath] ?? false;

            if (typeof value === 'object') {
              const children = (value.__order || Object.keys(value)).filter(k => k !== '__order');
              const completedCount = children.filter(k => completedDays.includes(k)).length;
              const percent = (completedCount / children.length) * 100;

              return (
                <li key={currentPath}>
                  <div className="book-node-wrapper">
                    <div className="book-node" onClick={() => toggleNode(currentPath)}>
                      <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
                      <strong>{key}</strong>
                      <span className="child-count">
                        ({children.length}{completedCount > 0 ? `, הושלמו ${completedCount}` : ''})
                      </span>
                    </div>
                    <div className="completion-bar" style={{ width: `${percent}%` }} />
                  </div>
                  {isOpen && renderBooks(value, currentPath, depth + 1)}
                </li>
              );
            } else {
              return (
                <li key={currentPath} className="book-leaf">
                  📘 {value} {completedDays.includes(key) && <span className="completed-mark">✔️</span>}
                </li>
              );
            }
          })}
      </ul>
    );
  };

  // ---------- Render Calendar ----------
 const renderCalendarView = () => {
  const selectedDateStr = selectedDate.toLocaleDateString('en-CA');
  // console.log(grouped[selectedDateStr])
  const chaptersOnSelected = grouped[selectedDateStr] || [];

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;

    return (
      <div className="tile-inner">
        <div className="hebrew-date">{getHebrewDateString(date, grouped)}</div>
      </div>
    );
  };

  

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateStr = date.toISOString().slice(0, 10);
    const items = grouped[dateStr];

    if (!items || items.length === 0) return null;
    if (items.length === 1) return 'react-calendar__tile--hasLearned';
    return 'react-calendar__tile--multiLearned';
  };


  const getChapterName = (chapterId) => {
    const dailyLearning = chabadBooks["לימוד יומי"];
    if (dailyLearning?.[chapterId]) return dailyLearning[chapterId];

    for (const topLevelKey in chabadBooks) {
      if (topLevelKey === "לימוד יומי") continue;

      const result = searchChapterIdInCategory(chabadBooks[topLevelKey], chapterId);
      if (result) return result;
    }

    return chapterId;
  };

  const searchChapterIdInCategory = (obj, chapterId) => {
    for (const key in obj) {
      if (key === "__order") continue;

      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        if (value[chapterId]) return `${key}, ${value[chapterId]}`;
        const deeperResult = searchChapterIdInCategory(value, chapterId);
        if (deeperResult) return deeperResult;
      }
    }
    return null;
  };

  return (
    <>
      <Calendar
        onChange={setSelectedDate}
        value={selectedDate}
        locale="he-IL"
        tileClassName={tileClassName}
        tileContent={tileContent}
      />

      <div className="calendar-details">
        <h3>{getHebrewDateString(selectedDate, grouped)}</h3>

        {chaptersOnSelected.length > 0 ? (
          <ul>
            {chaptersOnSelected.map((item, idx) => (
              <li key={idx}>📘 {getChapterName(item.chapterId)}</li>
            ))}
          </ul>
        ) : (
          <p>לא נלמדו פרקים ביום זה.</p>
        )}
      </div>
    </>
  );
};



  // ---------- Render ----------
  return (
    <div className="tanya-container">
      <HeaderComp userData={userData} menuHeaderOpen={menuHeaderOpen} setMenuHeaderOpen={setMenuHeaderOpen} />


      

      <h1 className="page-title">אזור אישי</h1>

      {/* הלימוד שלך */}
      <div className="section-container">
        <h2
          className="section-header"
          onClick={() => toggleSection('learning')}
          style={{ cursor: 'pointer' }}
        >
          {sectionsOpen.learning ? '▼' : '▶'} הלימוד שלך
        </h2>

        {sectionsOpen.learning && (
          <div className="calendar-container">
            {renderCalendarView()}
          </div>
        )}

      </div>


      <div className="section-container">
        <h2
          className="section-header"
          onClick={() => toggleSection('myFavoriteTehilim')}
          style={{ cursor: 'pointer' }}
        >
          {sectionsOpen.myFavoriteTehilim ? '▼' : '▶'} פרקי תהילים שלי
        </h2>

        {sectionsOpen.myFavoriteTehilim && (
          <div className="calendar-container">
            <FavoriteTehilimComp /> 
          </div>
        )}

      </div>

      <div className="section-container">
        <h2
          className="section-header"
          onClick={() => toggleSection('report')}
          style={{ cursor: 'pointer' }}
        >
          {sectionsOpen.report ? '▼' : '▶'} דוח לרבי
        </h2>

        {sectionsOpen.report && (
          <div className="calendar-container">
            <ReportToRabbiComp />
          </div>
        )}

      </div>

      <div className="section-container">
        <h2
          className="section-header"
          onClick={() => toggleSection('profile')}
          style={{ cursor: 'pointer' }}
        >
        {sectionsOpen.profile ? '▼' : '▶'} פרופיל
        </h2>

        {sectionsOpen.profile && <EditProfile />}

      </div>

      
      <button className="button" onClick={() => navigate('/tanya')} style={{ marginTop: 20 }}>
        ⬅ חזור
      </button> 


    </div>
  );
}

export default ProfileComp;
